import type { FunctionComponent } from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { config } from '../../../../config/config';
import { Button, ButtonSize } from '../../../shared/components/button/button';
import { Checkbox } from '../../../shared/components/checkbox/checkbox';
import { FormattedMessage } from '../../../shared/components/formatted-message/formatted-message';
import { useToast } from '../../../shared/hooks/use-toast';
import { createStyleHelper } from '../../../shared/utils/class-names';
import { getErrorMessage } from '../../../shared/utils/convert-error';
import type { IGetUserProfileQuery } from '../../queries/profile.graphql';
import { useUpdateUserProfileMutation } from '../../queries/profile.graphql';

import styles from './privacy-form.module.scss';

const style = createStyleHelper(styles, 'privacy-form');

type IUserProfile = NonNullable<IGetUserProfileQuery['userProfile']>;

export interface IConsentFormProps {
    isEmailAllowed: boolean;
    isPushNotificationAllowed: boolean;
    isSMSAllowed: boolean;
    isPostalMailAllowed: boolean;
    isCallAllowed: boolean;
}

export interface IPrivacyFormProps {
    userProfileId: string;
    fetchingUserProfile: boolean;
    userProfile: IUserProfile;
}

export const PrivacyForm: FunctionComponent<IPrivacyFormProps> = ({
    userProfileId,
    fetchingUserProfile,
    userProfile,
}) => {
    const intl = useIntl();
    const toast = useToast();

    const { register, handleSubmit } = useForm<IConsentFormProps>({
        mode: 'onChange',
        defaultValues: {
            isEmailAllowed: userProfile?.consents?.emailAllowed === 'Accepted',
            isPushNotificationAllowed:
                userProfile?.consents?.pushNotificationAllowed === 'Accepted',
            isSMSAllowed: userProfile?.consents?.smsAllowed === 'Accepted',
            isPostalMailAllowed: userProfile?.consents?.postalMailAllowed === 'Accepted',
            isCallAllowed: userProfile?.consents?.callAllowed === 'Accepted',
        },
    });

    const [{ fetching: isSubmitting }, execute] = useUpdateUserProfileMutation();

    const onSubmit = useCallback(
        async (userProfileConsentData: IConsentFormProps) => {
            const { error } = await execute({
                xTenantID: config.TENANT_ID,
                userProfileId: userProfileId,
                userProfile: {
                    isEmailAllowed: userProfileConsentData.isEmailAllowed,
                    isPushNotificationAllowed: userProfileConsentData.isPushNotificationAllowed,
                    isSMSAllowed: userProfileConsentData.isSMSAllowed,
                    isPostalMailAllowed: userProfileConsentData.isPostalMailAllowed,
                    isCallAllowed: userProfileConsentData.isCallAllowed,
                },
            });

            if (error) {
                toast.error(getErrorMessage(intl, 'user.promo.update', error));
                return;
            }

            toast.success(
                intl.formatMessage({
                    id: 'user.promo.update.success',
                    defaultMessage: 'User Promotional Medium Updated Successfully',
                }),
            );
            return;
        },
        [execute, userProfileId, toast, intl],
    );

    return (
        <form className={style()} onSubmit={handleSubmit(onSubmit)}>
            <Checkbox
                {...register('isEmailAllowed', {})}
                className={style('email')}
                label={
                    <FormattedMessage
                        id={'privacy-form.consent.email.name'}
                        defaultMessage={'isEmailAllowed'}
                    />
                }
            />
            <Checkbox
                {...register('isPushNotificationAllowed', {})}
                className={style('push')}
                label={
                    <FormattedMessage
                        id={'privacy-form.consent.on-site.name'}
                        defaultMessage={'isPushNotificationAllowed'}
                    />
                }
            />
            <Checkbox
                {...register('isSMSAllowed', {})}
                className={style('sms')}
                label={
                    <FormattedMessage
                        id={'privacy-form.consent.sms.name'}
                        defaultMessage={'isSMSAllowed'}
                    />
                }
            />
            <Checkbox
                {...register('isPostalMailAllowed', {})}
                className={style('postal')}
                label={
                    <FormattedMessage
                        id={'privacy-form.consent.mail.name'}
                        defaultMessage={'isPostalMailAllowed'}
                    />
                }
            />
            <Checkbox
                {...register('isCallAllowed', {})}
                className={style('call')}
                label={
                    <FormattedMessage
                        id={'privacy-form.consent.call.name'}
                        defaultMessage={'isCallAllowed'}
                    />
                }
            />
            <div className={style('button')}>
                <Button
                    className={style('button-save')}
                    size={ButtonSize.MEDIUM}
                    disabled={isSubmitting || fetchingUserProfile}
                    loading={isSubmitting || fetchingUserProfile}
                    type="submit"
                >
                    <FormattedMessage id="account.profile.save-button" defaultMessage="Save" />
                </Button>
            </div>
        </form>
    );
};
